import { Box, Stack, Typography, useTheme } from '@mui/material';
import { map } from 'lodash';
import numeral from 'numeral';
import { useMemo } from 'react';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import BarChart from 'src/components/HighChart/Barchart';
import { CreditCoinIcon } from 'src/components/Icons';
import { StyledLinearProgressCreditUsage } from 'src/features/Activities/common';
import { TypeActivitiesTab } from 'src/features/Activities/config';
import { useQueryParsedParams } from 'src/hooks/useQueryParam';
import colorSystem from 'src/scss/_colors-system.module.scss';
import { useQueryUsageInformation, useQueryUserCreditUsage } from 'src/services/queries';
import { calculatePercent, getNumberOrDefault } from 'src/utils/helpers/number';
import { getStringOrDefault } from 'src/utils/helpers/stringHelpers';

const CreditUsage = () => {
  const {
    palette: { divider, text },
    shape: { borderRadius },
  } = useTheme();
  const userUsage = useQueryUsageInformation();
  const { tab } = useQueryParsedParams();

  const payloadRequest = useMemo(() => {
    if (tab !== TypeActivitiesTab.TEAM_ACTIVITIES) return {};
    return {
      team_id: userUsage.data?.data?.team_id,
    };
  }, [tab]);

  const {
    data: { data },
    isFetching,
  } = useQueryUserCreditUsage(payloadRequest);

  const chart = useMemo(() => {
    const viewCredits = map(data, ({ total_view }) => getNumberOrDefault(total_view));
    const printCredits = map(data, ({ total_print }) => getNumberOrDefault(total_print));
    const downloadCredits = map(data, ({ total_download }) => getNumberOrDefault(total_download));
    const categories = map(data, ({ feature }) => getStringOrDefault(feature));
    return (
      <BarChart
        loading={isFetching}
        tooltipFormatter={function () {
          return `<b>${this.series.name}</b> <b>${this.y}</b>
          <b style="color: ${text.secondary}">credits</b>`;
        }}
        series={[
          {
            data: viewCredits,
            color: colorSystem.green500,
            type: 'column',
            showInLegend: false,
            borderRadius: 10,
            name: 'View',
          },
          {
            data: printCredits,
            color: colorSystem.orange500,
            showInLegend: false,
            type: 'column',
            borderRadius: 10,
            name: 'Print',
          },
          {
            data: downloadCredits,
            color: colorSystem.blue500,
            showInLegend: false,
            type: 'column',
            borderRadius: 10,
            name: 'Download',
          },
        ]}
        categories={categories}
        height={400}
      />
    );
  }, [data, isFetching]);

  return (
    <WhiteBoxContent p={2.5}>
      <Stack alignItems={'center'} direction={'row'} mb={3} justifyContent={'space-between'}>
        <Stack>
          <Typography variant="h6">Points Usage</Typography>
          <Typography variant="body2" color={'text.secondary'}>
            Points you spend each month
          </Typography>
        </Stack>

        <Box alignItems={'center'} display={'flex'} flex={1} maxWidth={700}>
          <StyledLinearProgressCreditUsage
            sx={{ flex: 1 }}
            value={calculatePercent(userUsage.data.data.current_usage, userUsage.data.data.usage_total)}
            color="secondary"
            variant="determinate"
          />
          <Typography ml={2} mr={1} color={'text.secondary'} variant="body2">
            {numeral(userUsage.data.data.current_usage).format('0,0')}/
            {numeral(userUsage.data.data.usage_total).format('0,0')}
          </Typography>
          <CreditCoinIcon fontSize="small" />
        </Box>
      </Stack>

      <Box borderRadius={borderRadius / 2} p={2} border={'1px solid' + divider}>
        <Stack mb={5} direction={'row'} justifyContent={'space-between'}>
          <Typography variant="subtitle1" color={'text.secondary'}>
            User Spending by Feature
          </Typography>

          {/* <TextField
            onChange={(e) => setParams({ ...params, ...{ feature_type_name: e.target.value } })}
            value={params.feature_type_name}
            select
            size="small"
          >
            {map(UserSpendingByFeatures, (feature) => (
              <MenuItem key={feature} value={feature}>
                {startCase(lowerCase(feature))}
              </MenuItem>
            ))}
          </TextField> */}
        </Stack>

        {chart}
      </Box>
    </WhiteBoxContent>
  );
};

export default CreditUsage;
